import React, { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { User } from '../../contexts';
import { EnumGenericJobStatus, TxnCountQuery } from '../../graphql/types';
import { useQueryAndRefetchOnUserChange } from '../../lib/hooks';
import { buildTransactionsLink, hrefOf } from '../../navigation/transactionsLink';
import { TXN_COUNT_DATA } from '../AllTransactions/queries';
import Button from '../Button';
import Modal from '../Modal';
import { useModalsAllowed } from '../Modal/useModalsAllowed';
import Text from '../Text';
import DataModelV2UpgradeModal from './index';
import { resolveUpgradeStep } from './resolveUpgradeStep';
import { UpgradeState } from './types';
import { UpgradeFlowContext } from './UpgradeFlowContext';
import useUpgradeFlow from './useUpgradeFlow';

interface Props {
  user: NonNullable<User>;
  children: ReactNode;
}

export default function AccountUpgradeFlow({ user, children }: Props) {
  const flow = useUpgradeFlow(user);
  const history = useHistory();
  // on a page without modals the flow keeps its state, and its modal opens once the user leaves the page
  const modalsAllowed = useModalsAllowed();
  const needsCounts = flow.status === 'ready' || flow.status === 'success';
  const loadCounts = needsCounts || flow.status === 'intro' || flow.status === 'upgrading';
  const counts = useQueryAndRefetchOnUserChange<TxnCountQuery>(TXN_COUNT_DATA, {
    skip: !loadCounts,
    fetchPolicy: 'network-only',
  });
  const transactionCount = (counts.data ?? counts.previousData)?.userTxnCount.totalCount;
  const taxYears = user.taxDetails.map(({ taxYear }) => taxYear);
  const firstUnlockedYear = Math.max(
    Math.min(...taxYears),
    user.lockedThroughYear == null ? -Infinity : user.lockedThroughYear + 1,
  );
  const taxYearCount = taxYears.length ? Math.max(0, Math.max(...taxYears) - firstUnlockedYear + 1) : 0;
  let state: UpgradeState | null = null;
  if (flow.status === 'intro') {
    state = { status: 'intro' };
  } else if (flow.status === 'ready' && transactionCount !== undefined) {
    state = {
      status: 'ready',
      transactionCount,
      lockedThroughYear: user.lockedThroughYear ?? undefined,
      email: user.email,
    };
  } else if (flow.status === 'success' && transactionCount !== undefined) {
    state = {
      status: 'success',
      transactionCount,
      taxYearCount,
    };
  } else if (flow.status === 'upgrading') {
    state = {
      status: 'upgrading',
      email: user.email,
      jobId: flow.job?.id,
      transactionCount,
      taxYearCount,
      currentStep:
        flow.job?.status === EnumGenericJobStatus.successful
          ? 'finalization'
          : resolveUpgradeStep(flow.job?.stage),
    };
  } else if (flow.status === 'failure') {
    state = { status: 'failure', email: user.email };
  }

  const seeTransactions = async () => {
    if (!(await flow.close())) return;
    const href = hrefOf(buildTransactionsLink('transferGroups', { kind: 'all' }));
    if (href) history.push(href);
  };

  return (
    <UpgradeFlowContext.Provider value={{ canOpen: flow.canOpen, open: flow.open }}>
      {children}
      {modalsAllowed && needsCounts && !state && (
        <Modal
          isOpen
          title="Account upgrade"
          loading={counts.loading}
          onClose={() => void flow.close()}
          showCloseIcon={false}
        >
          {counts.error && (
            <div className="space-y-4">
              <Text as="p" variant="muted">
                Unable to load your account details.
              </Text>
              <Button onClick={() => void counts.refetch().catch(() => {})}>Try again</Button>
            </div>
          )}
        </Modal>
      )}
      {modalsAllowed && state && (
        <DataModelV2UpgradeModal
          state={state}
          actionPending={flow.pending}
          onNotNow={flow.notNow}
          onContinue={flow.continueToConfirmation}
          onClose={() => void flow.close()}
          onStartUpgrade={() => void flow.start()}
          onRetry={() => void flow.start()}
          onSeeTransactions={() => void seeTransactions()}
          onContactSupport={() => window.Intercom('showNewMessage')}
        />
      )}
    </UpgradeFlowContext.Provider>
  );
}
